/**
 * Loading States Components
 * 
 * Comprehensive loading state components with different styles and animations
 * for various use cases throughout the application.
 * 
 * Features:
 * - Multiple loading styles (spinner, skeleton, pulse, dots)
 * - Size variants (small, medium, large)
 * - Context-aware loading states
 * - Accessibility compliant
 * - Performance optimized
 */

import React from 'react';

// Loading state types
export type LoadingSize = 'small' | 'medium' | 'large';
export type LoadingVariant = 'spinner' | 'skeleton' | 'pulse' | 'dots' | 'bars';

// Base loading props
interface BaseLoadingProps {
  size?: LoadingSize;
  className?: string;
  'aria-label'?: string;
  includeRole?: boolean;
}

// Spinner loading component
export const LoadingSpinner: React.FC<BaseLoadingProps> = ({ 
  size = 'medium', 
  className = '',
  includeRole = true,
  'aria-label': ariaLabel = 'Loading...'
}) => {
  const sizeClasses = {
    small: 'w-4 h-4',
    medium: 'w-8 h-8',
    large: 'w-12 h-12'
  };

  return (
    <div 
      className={`inline-block animate-spin rounded-full border-2 border-gray-300 border-t-blue-600 ${sizeClasses[size]} ${className}`}
      {...(includeRole && { role: "status", "aria-label": ariaLabel })}
    >
      <span className="sr-only">{ariaLabel}</span>
    </div>
  );
};

// Skeleton loading component
export const LoadingSkeleton: React.FC<BaseLoadingProps & {
  width?: string;
  height?: string;
  rounded?: boolean;
  includeRole?: boolean;
}> = ({ 
  size = 'medium',
  width,
  height,
  rounded = false,
  includeRole = true,
  className = '',
  'aria-label': ariaLabel = 'Loading content...'
}) => {
  const sizeClasses = {
    small: 'h-4',
    medium: 'h-6',
    large: 'h-8'
  };

  const widthClass = width || 'w-full';
  const heightClass = height || sizeClasses[size];
  const roundedClass = rounded ? 'rounded-full' : 'rounded';

  return (
    <div 
      className={`animate-pulse bg-gray-300 ${widthClass} ${heightClass} ${roundedClass} ${className}`}
      {...(includeRole && { role: "status", "aria-label": ariaLabel })}
    >
      <span className="sr-only">{ariaLabel}</span>
    </div>
  );
};

// Pulse loading component
export const LoadingPulse: React.FC<BaseLoadingProps> = ({ 
  size = 'medium',
  className = '',
  includeRole = true,
  'aria-label': ariaLabel = 'Loading...'
}) => {
  const sizeClasses = {
    small: 'w-2 h-2',
    medium: 'w-3 h-3',
    large: 'w-4 h-4'
  };

  return (
    <div 
      className={`flex space-x-1 ${className}`} 
      {...(includeRole && { role: "status", "aria-label": ariaLabel })}
    >
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className={`bg-blue-600 rounded-full animate-pulse ${sizeClasses[size]}`}
          style={{
            animationDelay: `${i * 0.2}s`,
            animationDuration: '1s'
          }}
        />
      ))}
      <span className="sr-only">{ariaLabel}</span>
    </div>
  );
};

// Dots loading component
export const LoadingDots: React.FC<BaseLoadingProps> = ({ 
  size = 'medium',
  className = '',
  includeRole = true,
  'aria-label': ariaLabel = 'Loading...'
}) => {
  const sizeClasses = {
    small: 'w-1 h-1',
    medium: 'w-2 h-2',
    large: 'w-3 h-3'
  };

  return (
    <div 
      className={`flex space-x-1 ${className}`} 
      {...(includeRole && { role: "status", "aria-label": ariaLabel })}
    >
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className={`bg-blue-600 rounded-full animate-bounce ${sizeClasses[size]}`}
          style={{
            animationDelay: `${i * 0.1}s`
          }}
        />
      ))}
      <span className="sr-only">{ariaLabel}</span>
    </div>
  );
};

// Bars loading component
export const LoadingBars: React.FC<BaseLoadingProps> = ({ 
  size = 'medium',
  className = '',
  includeRole = true,
  'aria-label': ariaLabel = 'Loading...'
}) => {
  const sizeClasses = {
    small: 'w-1 h-4',
    medium: 'w-1 h-6',
    large: 'w-2 h-8'
  };

  return (
    <div 
      className={`flex space-x-1 items-end ${className}`} 
      {...(includeRole && { role: "status", "aria-label": ariaLabel })}
    >
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          className={`bg-blue-600 animate-pulse ${sizeClasses[size]}`}
          style={{
            animationDelay: `${i * 0.15}s`,
            animationDuration: '1.2s'
          }}
        />
      ))}
      <span className="sr-only">{ariaLabel}</span>
    </div>
  );
};

// Product card skeleton
export const ProductCardSkeleton: React.FC<{ className?: string }> = ({ className = '' }) => {
  return (
    <div className={`border rounded-lg p-4 space-y-4 ${className}`} role="status" aria-label="Loading product...">
      <LoadingSkeleton height="h-48" rounded={false} className="w-full" includeRole={false} />
      <div className="space-y-2">
        <LoadingSkeleton height="h-4" width="w-3/4" includeRole={false} />
        <LoadingSkeleton height="h-4" width="w-1/2" includeRole={false} />
        <LoadingSkeleton height="h-6" width="w-1/4" includeRole={false} />
      </div>
      <LoadingSkeleton height="h-10" width="w-full" rounded={false} includeRole={false} />
      <span className="sr-only">Loading product...</span>
    </div>
  );
};

// Product grid skeleton
export const ProductGridSkeleton: React.FC<{ 
  count?: number;
  className?: string;
}> = ({ count = 8, className = '' }) => {
  return (
    <div 
      className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 ${className}`}
      role="status" 
      aria-label={`Loading ${count} products...`}
    >
      {Array.from({ length: count }).map((_, i) => (
        <ProductCardSkeleton key={i} />
      ))}
      <span className="sr-only">Loading {count} products...</span>
    </div>
  );
};

// Cart skeleton
export const CartSkeleton: React.FC<{ className?: string }> = ({ className = '' }) => {
  return (
    <div className={`space-y-4 ${className}`} role="status" aria-label="Loading cart...">
      {[1, 2, 3].map((i) => (
        <div key={i} className="flex space-x-4 p-4 border rounded">
          <LoadingSkeleton width="w-16" height="h-16" />
          <div className="flex-1 space-y-2">
            <LoadingSkeleton height="h-4" width="w-3/4" />
            <LoadingSkeleton height="h-4" width="w-1/2" />
            <LoadingSkeleton height="h-4" width="w-1/4" />
          </div>
        </div>
      ))}
      <div className="border-t pt-4">
        <LoadingSkeleton height="h-6" width="w-1/3" className="ml-auto" />
      </div>
      <span className="sr-only">Loading cart...</span>
    </div>
  );
};

// Page loading overlay
export const PageLoadingOverlay: React.FC<{
  isVisible: boolean;
  message?: string;
  variant?: LoadingVariant;
}> = ({ 
  isVisible, 
  message = 'Loading...', 
  variant = 'spinner' 
}) => {
  if (!isVisible) return null;

  const LoadingComponent = {
    spinner: LoadingSpinner,
    skeleton: LoadingSkeleton,
    pulse: LoadingPulse,
    dots: LoadingDots,
    bars: LoadingBars
  }[variant];

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      role="status"
      aria-label={message}
    >
      <div className="bg-white rounded-lg p-6 flex flex-col items-center space-y-4">
        <LoadingComponent size="large" includeRole={false} />
        <p className="text-gray-700 font-medium">{message}</p>
      </div>
    </div>
  );
};

// Inline loading component with text
export const InlineLoading: React.FC<{
  message?: string;
  variant?: LoadingVariant;
  size?: LoadingSize;
  className?: string;
}> = ({ 
  message = 'Loading...', 
  variant = 'spinner',
  size = 'medium',
  className = ''
}) => {
  const LoadingComponent = {
    spinner: LoadingSpinner,
    skeleton: LoadingSkeleton,
    pulse: LoadingPulse,
    dots: LoadingDots,
    bars: LoadingBars
  }[variant];

  return (
    <div className={`flex items-center space-x-3 ${className}`} role="status" aria-label={message}>
      <LoadingComponent size={size} includeRole={false} />
      <span className="text-gray-600">{message}</span>
    </div>
  );
};

// Button loading state
export const LoadingButton: React.FC<{
  isLoading: boolean;
  children: React.ReactNode;
  disabled?: boolean;
  onClick?: () => void;
  className?: string;
  loadingText?: string;
}> = ({ 
  isLoading, 
  children, 
  disabled = false,
  onClick,
  className = '',
  loadingText = 'Loading...'
}) => {
  return (
    <button
      onClick={onClick}
      disabled={disabled || isLoading}
      className={`relative flex items-center justify-center space-x-2 ${className} ${
        (disabled || isLoading) ? 'opacity-50 cursor-not-allowed' : ''
      }`}
      aria-label={isLoading ? loadingText : undefined}
    >
      {isLoading && (
        <LoadingSpinner size="small" className="absolute left-3" />
      )}
      <span className={isLoading ? 'ml-6' : ''}>
        {isLoading ? loadingText : children}
      </span>
    </button>
  );
};

// Content loading wrapper
export const LoadingWrapper: React.FC<{
  isLoading: boolean;
  error?: Error | null;
  children: React.ReactNode;
  loadingComponent?: React.ReactNode;
  errorComponent?: React.ReactNode;
  className?: string;
}> = ({ 
  isLoading, 
  error, 
  children, 
  loadingComponent,
  errorComponent,
  className = ''
}) => {
  if (error && errorComponent) {
    return <div className={className}>{errorComponent}</div>;
  }

  if (isLoading) {
    return (
      <div className={className}>
        {loadingComponent || <InlineLoading />}
      </div>
    );
  }

  return <div className={className}>{children}</div>;
};

// Export all components
export {
  LoadingSpinner as Spinner,
  LoadingSkeleton as Skeleton,
  LoadingPulse as Pulse,
  LoadingDots as Dots,
  LoadingBars as Bars
};
